import * as React from 'react'
import {
  Button,
  Radio,
  Skeleton,
  message,
  Input,
  Select,
  Icon,
  Dropdown,
  Menu,
  Checkbox,
  Divider,
} from 'antd'
import { Link, browserHistory } from 'react-router'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { CurrentUserStore } from '../../../../stores/CurrentUserStore'
import {
  IProductSearchFilter,
  IDuplicateProductResult,
  IDuplicateList,
  PermissionNames,
  API,
} from '@getgreenline/homi-shared'
import { productApi, ProductModels } from '@getgreenline/products'
import { Routes } from '../../../../constants/Routes'
import AppPagination from '../../../../components/Pagination/index'
import { AppView, AppMainView } from '../../../../components/AppView'
import AppHeader from '../../../../components/AppView/Header'
import { ProductFilter } from './ProductFilter'
import { ProductStore } from '../../../../stores/CurrentCompanyStore/ProductStore'
import { SupplierStore } from '../../../../stores/CurrentCompanyStore/SupplierStore'
import { LocalStorage, IExtraFields } from '../../../../utilities/LocalStorage'
import { EmptyProductListing } from './EmptyProductListing'
import { DuplicateProductAlert } from './DuplicateProductAlert'
import { ProductList } from './ProductList'

import { ProductLogDrawer } from '../ProductLogDrawer'

import './index.scss'
import { HelpLinkMenu } from '../../../../components/GrDropdown/HelpLinkMenu/HelpLinkMenu'
import { BlazeButton } from '../../../../components/CustomButtons/BlazeButton'
import Helmet from 'react-helmet'
import { getCategoryName, parseErrorMsg, GrRegex } from '../../../../utilities/helpers'
import { debounce } from 'lodash'

import { ProductColumnDropdown } from './ProductColumnDropdown'
import { OptionProps } from 'antd/lib/select'
import { ImportExportProductsModal } from './ImportExportProductsModal'
import { SupplierSelect } from './SupplierSelect'
import { CategorySelect } from './CategorySelect'
import { StrainTypeSelect } from './StrainTypeSelect'
import FilterSVG from './../../../../styles/icons/Filter.svg'
import { CheckboxChangeEvent } from 'antd/lib/checkbox'
import { TagSelect } from './TagSelect'
import { HelpLinks } from '../../../../constants/ExternalLinks'
import { Alert } from 'antd-v4'
import { ArrowRightOutlined } from '@ant-design/icons'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
}

export enum EOptionalFilters {
  STRAIN_TYPE = 'Strain type',
  TAGS = 'Tags',
}

export type ProductStatusType = 'active' | 'inactive' | 'deleted'
interface State {
  showModal: boolean
  perPage: number
  total: number
  duplicatingProduct?: ProductModels.INestedProduct
  expandedProductIds: string[]
  defaultExpanded: boolean
  expanded: boolean
  showProductLogs: boolean
  extraFields: IExtraFields
  extraFieldsSelected: IExtraFields
  duplicateSkuMap: IDuplicateList
  duplicateBarcodeMap: IDuplicateList
  emptyTaxProductsMap: IDuplicateList
  productStatus: ProductStatusType
  productStatusSelected: ProductStatusType
  isModalVisible: boolean
  applyLoading: boolean
  selectedFilters: string[]
  selectedFiltersDropdownVisible: boolean
}

type extraFieldKeys = keyof IExtraFields

export interface IExtraFieldOption {
  value: extraFieldKeys
  label: string
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class ProductListing extends React.Component<React.PropsWithChildren<Props>, State> {
  productFilter: ProductFilter
  companyStore: CurrentCompanyStore = this.props.currentCompanyStore!
  productStore: ProductStore = this.props.currentCompanyStore!.productStore!
  supplierStore: SupplierStore = this.props.currentCompanyStore!.supplierStore!

  extraFieldOptions: IExtraFieldOption[] = [
    { value: 'supplierName', label: 'Brand' },
    { value: 'tags', label: 'Tags' },
    { value: 'weight', label: 'Net product weight' },
    { value: 'cannabisWeight', label: 'Cannabis weight' },
    { value: 'cannabisVolume', label: 'Cannabis volume' },
    { value: 'thc', label: 'THC' },
    { value: 'cbd', label: 'CBD' },
    { value: 'strainType', label: 'Strain type' },
    { value: 'barcode', label: 'Barcode' },
    { value: 'createDate', label: 'Created at' },
    { value: 'updateDate', label: 'Updated at' },
    { value: 'purchasePrice', label: 'Cost' },
  ]

  widthBreakPoint = 800

  constructor(props: Props) {
    super(props)

    if (!this.productStore.productFilter) {
      this.productStore.productFilter = new ProductFilter(browserHistory)
    }

    this.productFilter = this.productStore.productFilter

    this.state = {
      showModal: true,
      perPage: this.productFilter.paginationFilter.limit,
      expandedProductIds: [],
      defaultExpanded: LocalStorage.getProductListingExpanded(),
      expanded: LocalStorage.getProductListingExpanded(),
      total: 0,
      showProductLogs: false,
      extraFields: LocalStorage.getProductExtraFields(),
      extraFieldsSelected: LocalStorage.getProductExtraFields(),
      duplicateSkuMap: {},
      duplicateBarcodeMap: {},
      emptyTaxProductsMap: {},
      productStatus: this.currentProductStatus,
      productStatusSelected: this.currentProductStatus,
      isModalVisible: false,
      applyLoading: false,
      selectedFilters: [],
      selectedFiltersDropdownVisible: false,
    }
  }

  get currentProductStatus() {
    switch (true) {
      case this.productFilter.productFilter.isDeleted:
        return 'deleted'
      case !this.productFilter.productFilter.isActive:
        return 'inactive'
      default:
        return 'active'
    }
  }

  async componentDidMount() {
    this.productStore.getCategories()
    this.productStore.getTaxGroups()
    this.supplierStore.getSuppliers()
    this.getDuplicatesSkuAndBarcode()
    this.getEmptyTaxGroupProducts()
    this.loadProducts()
    const { productFilter } = this.productFilter
    const optionalFilters = productFilter?.selectedOptionalFilters || []
    this.productFilter.setSelectedOptionalFilters(optionalFilters)
  }

  toggleExtraField = (fieldKey: string, fieldCheckedStatus: boolean, selectAll?: boolean) => {
    const key = fieldKey

    if (selectAll !== undefined) {
      this.setState(
        {
          extraFieldsSelected: Object.keys(this.state.extraFieldsSelected).reduce(
            (acc, curr) => ({ ...acc, [curr]: selectAll }),
            this.state.extraFieldsSelected,
          ),
        },
        () => {
          LocalStorage.setProductExtraFields(this.state.extraFieldsSelected)
        },
      )
      return
    }

    this.setState(
      {
        extraFieldsSelected: {
          ...this.state.extraFieldsSelected,
          [key]: fieldCheckedStatus,
        },
      },
      () => {
        LocalStorage.setProductExtraFields(this.state.extraFieldsSelected)
      },
    )
  }

  countExtraFieldsSelected = () => {
    const { extraFields } = this.state
    const selectedFields = Object.keys(extraFields).filter((field) => extraFields[field])
    return selectedFields.length
  }
  refreshPage = () => {
    this.getDuplicatesSkuAndBarcode()
    this.getEmptyTaxGroupProducts()
    this.loadProducts()
    this.props.currentCompanyStore?.getTags()
  }

  getDuplicatesSkuAndBarcode = async () => {
    const company = this.props.currentCompanyStore!.company!
    const duplicateProductsResult: IDuplicateProductResult = await API.getDuplicatesSkuAndBarcode(
      company.id,
    )

    this.setState({
      duplicateSkuMap: duplicateProductsResult.sku,
      duplicateBarcodeMap: duplicateProductsResult.barcode,
    })
  }

  getEmptyTaxGroupProducts = async () => {
    const company = this.props.currentCompanyStore!.company!
    const emptyTaxProductsRes: IDuplicateList = await productApi.getEmptyTaxGroupProducts(
      company.id,
    )

    this.setState({
      emptyTaxProductsMap: emptyTaxProductsRes,
    })
  }

  loadProducts = async () => {
    const { selectedCategoryIds, isActive, isDeleted } = this.productFilter.productFilter

    const searchFilters: IProductSearchFilter = {
      ...this.productFilter.productFilter,
      ...this.productFilter.paginationFilter,
      selectedCategoryIds: selectedCategoryIds ? selectedCategoryIds : [],
    }

    // if limit is not a number, set the limit to 20
    if (isNaN(this.productFilter.paginationFilter.limit)) {
      this.productFilter.paginationFilter.limit = 20
    }

    let total: number
    let perPage: number
    this.productStore.resetProducts()

    switch (true) {
      case isDeleted:
        try {
          const deletedProducts = await this.productStore.getDeletedProducts(searchFilters)
          total = deletedProducts.total
          perPage = deletedProducts.limit
          this.setState({ total, perPage })
        } catch (error) {
          message.error(parseErrorMsg(error) || 'There was an error getting deleted products')
        }
        break
      case isActive:
        try {
          const allProducts = await this.productStore.getProducts(searchFilters)
          total = allProducts.total
          perPage = allProducts.limit
          this.setState({ total, perPage })
        } catch (error) {
          message.error(parseErrorMsg(error) || 'There was an error getting active products')
        }
        break
      default:
        try {
          const inactiveProducts = await this.productStore.getInactiveProducts(searchFilters)
          total = inactiveProducts.total
          perPage = inactiveProducts.limit
          this.setState({ total, perPage })
        } catch (error) {
          message.error(parseErrorMsg(error) || 'There was an error getting inactive products')
        }
    }

    this.productFilter.applyFilters()
  }

  save() {
    this.setState({
      showModal: false,
    })
  }

  closeModal() {
    this.setState({
      showModal: false,
    })
  }

  setDuplicatingProduct = (product?: ProductModels.INestedProduct) =>
    this.setState({ duplicatingProduct: product })

  private async handlePaginationClick(data: any) {
    const selected = data.selected
    const offset = Math.ceil(selected * this.state.perPage)
    this.productFilter.setOffset(offset)
    this.loadProducts()
  }

  toggleDefaultExpanded(value: boolean) {
    this.setState({ defaultExpanded: value, expandedProductIds: [] })
    LocalStorage.setProductListingExpanded(value)
  }

  onFilterChange = debounce(this.loadProducts, 1000)

  toggleProductStatus(status: ProductStatusType) {
    if (status === 'deleted') {
      this.productFilter.setIsDeleted(true)
    } else {
      this.productFilter.setIsDeleted(false)
      this.productFilter.setIsActive(status === 'active')
    }

    this.setState({ productStatusSelected: status })
  }

  onRefreshPage = debounce(this.refreshPage, 500)

  resetProductFilters = () => {
    this.productFilter.reset()

    this.setState({
      productStatusSelected: 'active',
      productStatus: 'active',
    })

    this.loadProducts()
  }

  delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

  applyProductFilters = async () => {
    this.setState({ applyLoading: true })
    this.setState({ productStatus: this.state.productStatusSelected })
    this.productFilter.setOffset(0)
    await this.onFilterChange()

    // Need to delay the view related state changes to allow the product listing to update
    await this.delay(1000)
    this.toggleDefaultExpanded(this.state.expanded)
    this.setState({ extraFields: this.state.extraFieldsSelected })
    this.setState({ applyLoading: false })
  }

  render() {
    const {
      duplicateSkuMap,
      duplicateBarcodeMap,
      emptyTaxProductsMap,
      showProductLogs,
      isModalVisible,
    } = this.state
    const duplicateSkuMapKeys = Object.keys(duplicateSkuMap)
    const duplicateBarcodeMapKeys = Object.keys(duplicateBarcodeMap)
    const emptyTaxProductsKeys = Object.keys(emptyTaxProductsMap)
    const { productFilter, paginationFilter } = this.productFilter
    const { searchQuery } = productFilter
    const company = this.props.currentCompanyStore!.company!
    const canEditProducts = this.props.currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_EDIT_PRODUCTS,
      company.id,
    )
    const {
      flattenedCategories,
      taxGroups,
      inactiveNestedProducts,
      nestedProducts,
      deletedNestedProducts,
    } = this.productStore

    const products =
      this.state.productStatus === 'deleted'
        ? deletedNestedProducts
        : this.state.productStatus === 'active'
        ? nestedProducts
        : inactiveNestedProducts
    const suppliersWithName = this.supplierStore.suppliersWithName

    let pageCount
    let currentPage
    if (
      products &&
      flattenedCategories &&
      suppliersWithName &&
      taxGroups &&
      products.length !== 0
    ) {
      // Pagination
      const { offset } = this.productFilter.paginationFilter
      const { total, perPage } = this.state
      pageCount = Math.ceil(total / perPage)
      currentPage = Math.ceil(offset / perPage)
    }

    const flattenedCategoriesByName = flattenedCategories.map((category) => {
      return {
        ...category,
        name: getCategoryName(category),
      }
    })

    const filterOption = (input: string, option: React.ReactElement<OptionProps>) => {
      const optionName = option.props.title

      if (optionName && typeof optionName === 'string') {
        const lowerCaseInput = input.toLowerCase()

        return optionName.toLowerCase().indexOf(lowerCaseInput) >= 0
      }

      return true
    }

    const onSelectAllFilterOption = (e: CheckboxChangeEvent, filters: EOptionalFilters[]) => {
      const { checked } = e.target
      if (checked) {
        this.productFilter.setSelectedOptionalFilters(filters)
      } else {
        this.productFilter.setSelectedOptionalFilters([])
        this.productFilter.resetOptionalFilters()
      }
    }

    const filterDropdownOverlay = () => {
      const filters = Object.entries(EOptionalFilters).map(([key, value]) => value)

      return (
        <Menu className='gr-filter'>
          <Menu.Item>
            <Checkbox
              checked={productFilter.selectedOptionalFilters?.length === filters.length}
              onChange={(e) => {
                onSelectAllFilterOption(e, filters)
              }}
              style={{ fontWeight: 'bold' }}
            >
              Select All
            </Checkbox>
          </Menu.Item>
          <div className='px-2'>
            <Divider style={{ marginTop: 2, marginBottom: 2 }} />
          </div>
          {filters.map((filterOption) => {
            return (
              <Menu.Item key={filterOption}>
                <Checkbox
                  checked={productFilter.selectedOptionalFilters?.includes(filterOption)}
                  onChange={(e) => {
                    onChangeFilterOption(e, filterOption)
                  }}
                >
                  {filterOption}
                </Checkbox>
              </Menu.Item>
            )
          })}
        </Menu>
      )
    }

    const onChangeFilterOption = (e: CheckboxChangeEvent, filter: EOptionalFilters) => {
      const { selectedOptionalFilters } = productFilter
      if (e.target.checked) {
        if (selectedOptionalFilters) {
          this.productFilter.setSelectedOptionalFilters([...selectedOptionalFilters, filter])
        } else {
          this.productFilter.setSelectedOptionalFilters([filter])
        }
      } else {
        if (selectedOptionalFilters) {
          this.productFilter.setSelectedOptionalFilters(
            selectedOptionalFilters.filter((filterToRemove: string) => filterToRemove !== filter),
          )
          if (filter === EOptionalFilters.TAGS) {
            this.productFilter.setSelectedTagIds(undefined)
            return
          }
          if (filter === EOptionalFilters.STRAIN_TYPE) {
            this.productFilter.setSelectedStrainTypes(undefined)
            return
          }
        }
      }
    }

    return (
      <AppView column>
        <Helmet>
          <title>Products | Product List</title>
          <meta name='page-loaded' content='true' />
        </Helmet>
        <AppHeader title='Product list'>
          <span className='spaced-button-wrapper'>
            <HelpLinkMenu
              helpLinkMenus={[
                {
                  href: HelpLinks.PRODUCTS_ADD_EDIT,
                  title: 'Adding & editing products',
                },
              ]}
            />

            <BlazeButton
              buttonProps={{
                icon: 'download',
                onClick: () => this.setState({ isModalVisible: true }),
              }}
              titleProps={{
                title: 'Import/Export',
                widthBreakPoint: this.widthBreakPoint,
              }}
            />

            <BlazeButton
              buttonProps={{
                icon: 'history',
                onClick: () => this.setState({ showProductLogs: true }),
              }}
              titleProps={{
                title: 'Edit history',
                widthBreakPoint: this.widthBreakPoint,
              }}
            />

            <BlazeButton
              buttonProps={{
                id: 'add-product-button',
                type: 'primary',
                icon: 'plus',
                onClick: () => {
                  browserHistory.push(Routes.CREATE_PRODUCT(company.id, window.location.search))
                },
                disabled: !canEditProducts,
              }}
              titleProps={{
                title: `Add product${canEditProducts ? '' : ' (no permissions)'}`,
                widthBreakPoint: this.widthBreakPoint,
              }}
            />

            <ImportExportProductsModal
              isModalVisible={isModalVisible}
              closeModal={() => this.setState({ isModalVisible: false })}
              afterImport={this.refreshPage}
            />
          </span>
        </AppHeader>

        <AppMainView className='background-grey intercom-pb-2'>
          <Alert
            className='mb-2'
            style={{ marginTop: -8 }}
            message={
              <Link
                to={Routes.DASHBOARD_SAMPLE_DIRECT(company?.id)}
                style={{ color: 'black', textDecorationLine: 'underline' }}
              >
                Check out the latest samples
                <ArrowRightOutlined style={{ marginLeft: '6px' }} />
              </Link>
            }
            type='success'
          />
          <div className='white-rounded-container' style={{ paddingBottom: 0 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              <Dropdown
                className='gr-filter-column-item gr-filter-gutter'
                trigger={['click']}
                overlay={filterDropdownOverlay}
                onVisibleChange={() =>
                  this.setState({
                    selectedFiltersDropdownVisible: !this.state.selectedFiltersDropdownVisible,
                  })
                }
                visible={this.state.selectedFiltersDropdownVisible}
              >
                <Button
                  type={this.state.selectedFiltersDropdownVisible ? 'default' : 'link'}
                  className='pr-5 gr-filter-button'
                  style={{ height: 34 }}
                >
                  <img src={FilterSVG} style={{ width: 25 }} alt='' />
                </Button>
              </Dropdown>

              <Input
                className='gr-filter-item gr-filter-gutter antd-v3-input-override'
                prefix={<Icon type='search' />}
                placeholder='ID, name, SKU'
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  this.productFilter.setSearchQuery(
                    e.target.value.replace(GrRegex.unicodeRegex, ''),
                  )
                  this.productFilter.setOffset(0)
                }}
                onPressEnter={this.applyProductFilters}
              />

              <CategorySelect
                productFilter={this.productFilter}
                flattenedCategoriesByName={flattenedCategoriesByName}
                filterOption={filterOption}
              />

              <SupplierSelect
                productFilter={this.productFilter}
                suppliersWithName={suppliersWithName}
                filterOption={filterOption}
              />

              {productFilter.selectedOptionalFilters?.includes(EOptionalFilters.STRAIN_TYPE) && (
                <StrainTypeSelect productFilter={this.productFilter} />
              )}

              {productFilter.selectedOptionalFilters?.includes(EOptionalFilters.TAGS) && (
                <TagSelect
                  tags={this.companyStore.tags}
                  productFilter={this.productFilter}
                  filterOption={filterOption}
                />
              )}

              <Select<string>
                className='gr-filter-item gr-filter-gutter antd-v3-select-override'
                value={this.state.expanded ? 'expand' : 'collapse'}
                optionLabelProp='label'
                onChange={(value) => {
                  if (value === 'expand') {
                    this.setState({ expanded: true })
                  } else {
                    this.setState({ expanded: false })
                  }
                }}
              >
                <Select.Option key={1} value='collapse' label={'Collapsed variants'}>
                  <Radio checked={!this.state.expanded}>Collapsed variants</Radio>
                </Select.Option>
                <Select.Option key={2} value='expand' label={'Expanded variants'}>
                  <Radio checked={this.state.expanded}>Expanded variants</Radio>
                </Select.Option>
              </Select>

              <Select<ProductStatusType>
                className='gr-filter-item gr-filter-gutter antd-v3-select-override'
                value={this.state.productStatusSelected}
                optionLabelProp='label'
                onChange={(value) => {
                  this.toggleProductStatus(value)
                }}
              >
                <Select.Option key={1} value={'active'} label={'Active products'}>
                  <Radio checked={this.state.productStatusSelected === 'active'}>
                    Active products
                  </Radio>
                </Select.Option>
                <Select.Option key={2} value={'inactive'} label={'Inactive products'}>
                  <Radio checked={this.state.productStatusSelected === 'inactive'}>
                    Inactive products
                  </Radio>
                </Select.Option>
                <Select.Option key={3} value={'deleted'} label={'Deleted products'}>
                  <Radio checked={this.state.productStatusSelected === 'deleted'}>
                    Deleted products
                  </Radio>
                </Select.Option>
              </Select>

              <ProductColumnDropdown
                extraFields={this.state.extraFieldsSelected}
                extraFieldOptions={this.extraFieldOptions}
                onChange={this.toggleExtraField}
              />

              <Select<number>
                className='gr-filter-item gr-filter-gutter antd-v3-select-override'
                style={{ width: '138px' }}
                value={paginationFilter.limit}
                optionLabelProp='label'
                onChange={(value) => {
                  this.productFilter.setLimit(value)
                }}
              >
                <Select.Option key={20} value={20} label={'20 per page'}>
                  <Radio checked={paginationFilter.limit === 20}>20 per page</Radio>
                </Select.Option>
                <Select.Option key={30} value={30} label={'30 per page'}>
                  <Radio checked={paginationFilter.limit === 30}>30 per page</Radio>
                </Select.Option>
                <Select.Option key={40} value={40} label={'40 per page'}>
                  <Radio checked={paginationFilter.limit === 40}>40 per page</Radio>
                </Select.Option>
                <Select.Option key={50} value={50} label={'50 per page'}>
                  <Radio checked={paginationFilter.limit === 50}>50 per page</Radio>
                </Select.Option>
              </Select>

              <BlazeButton
                buttonProps={{
                  type: 'primary',
                  onClick: this.applyProductFilters,
                  loading: this.state.applyLoading,
                }}
                titleProps={{
                  title: `Apply`,
                }}
              />

              <Button
                type='link'
                className='mb-2'
                disabled={this.productFilter.resetDisabled()}
                onClick={() => this.resetProductFilters()}
              >
                Reset
              </Button>
            </div>

            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
              }}
            >
              {duplicateSkuMapKeys.length > 0 && (
                <DuplicateProductAlert
                  DuplicateProductsMap={duplicateSkuMap}
                  text='duplicate sku'
                  DuplicateListKey='sku'
                />
              )}

              {duplicateBarcodeMapKeys.length > 0 && (
                <DuplicateProductAlert
                  DuplicateProductsMap={duplicateBarcodeMap}
                  text='duplicate barcode'
                  DuplicateListKey='barcode'
                />
              )}

              {emptyTaxProductsKeys.length > 0 && (
                <DuplicateProductAlert
                  DuplicateProductsMap={emptyTaxProductsMap}
                  text='empty tax group'
                  hideKeyColumn
                />
              )}
            </div>
          </div>

          {products === undefined ||
          flattenedCategories === undefined ||
          suppliersWithName === undefined ||
          taxGroups === undefined ? (
            <Skeleton active />
          ) : products.length === 0 ? (
            <>
              {productFilter.isActive ? (
                <EmptyProductListing
                  companyId={company.id}
                  flattenedCategories={flattenedCategories}
                  suppliers={suppliersWithName}
                  taxGroups={taxGroups}
                />
              ) : (
                <p className='text-muted'>You have no inactive products</p>
              )}

              {this.props.children}
            </>
          ) : (
            <ProductList
              products={products}
              productFilter={this.productFilter}
              extraFields={this.state.extraFields}
              extraFieldOptions={this.extraFieldOptions}
              duplicatingProduct={this.state.duplicatingProduct}
              canEditProducts={canEditProducts}
              taxGroups={taxGroups}
              onChange={this.onRefreshPage}
              setDuplicatingProduct={this.setDuplicatingProduct}
              defaultExpanded={this.state.defaultExpanded}
              productStatus={this.state.productStatus}
            />
          )}

          {currentPage !== undefined && pageCount !== undefined && (
            <AppPagination
              currentPage={currentPage}
              pageCount={pageCount}
              onPageChange={this.handlePaginationClick.bind(this)}
            />
          )}
        </AppMainView>

        {products && flattenedCategories && suppliersWithName && taxGroups && this.props.children}

        <ProductLogDrawer
          companyId={company.id}
          visible={showProductLogs}
          onClose={() => this.setState({ showProductLogs: false })}
        />
      </AppView>
    )
  }
}
